import { useTranslation } from "gatsby-plugin-react-i18next";
import { useState, type ChangeEvent } from "react";
import SingleInputForm from "../../components/general/SingleInputForm";
import { useAppState } from "../../context/app/context";
import { hasCookieConsent } from "../../utils/cookies/cookie-consent-service";
import { emailRegex } from "../../utils/regex";

const verifyEmail = (email: string) => emailRegex.test(email);

interface NewsLetterProps {
	list_id: string;
	placeholder?: string;
}

const Newsletter = ({ list_id, placeholder }: NewsLetterProps) => {
	const { siteConfig } = useAppState();
	const { t } = useTranslation();

	const [loading, setLoading] = useState<boolean>(false);
	const [successMessage, setSuccessMessage] = useState<string>("");
	const [errorMessage, setErrorMessage] = useState<string>("");
	const [email, setEmail] = useState<string>("");

	const sendToKlaviyo = async () => {
		return fetch("/api/klaviyo/newsletter-signup", {
			method: "POST",
			headers: {
				"content-type": "application/json",
			},
			body: JSON.stringify({
				email: email,
				list_id: list_id,
			}),
		});
	};

	const onEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
		setEmail(event.target.value.trim());
		setErrorMessage("");
	};

	const onFormSubmit = async () => {
		const verified = verifyEmail(email);
		setLoading(true);

		try {
			if (verified) {
				setErrorMessage("");

				const response = await sendToKlaviyo();

				if (response.status === 200) {
					setSuccessMessage(t("You have successfully signed up!"));
					if (window.klaviyo && hasCookieConsent("targeting")) {
						window.klaviyo.identify({
							$email: email,
							locale: `${
								siteConfig.language.split("-")[0]
							}-${siteConfig.store.toUpperCase()}`,
							lang: siteConfig.language.toUpperCase().split("-")[0] ?? "",
							currency: siteConfig.currency,
						});
					}
					setEmail("");
				} else {
					setErrorMessage(t("Something went wrong"));
				}
			} else {
				setErrorMessage(t("Please enter a valid email address"));
			}
		} finally {
			setLoading(false);
		}
	};

	return (
		<SingleInputForm
			id="newsletter-signup-form"
			errorMessage={errorMessage}
			successMessage={successMessage}
			placeholder={placeholder ?? t("Email")}
			buttonLabel={t("Send")}
			loading={loading}
			onFormSubmit={onFormSubmit}
			onInputChange={onEmailChange}
			input={email}
			removeButtonLabel={t("Remove")}
		/>
	);
};

export default Newsletter;
